import { useDataInputPanels } from "./useDataInputPanels";
import { KahunPanelSubGroup, Relations } from "shared";
import { QualifiedFeatureFragment } from "fe-common/graphql";
import { DataInputPanel } from "../dataInputTypes";
import { useMemo } from "react";
import { useSmartPanels } from "./useSmartPanels";

interface PanelInterface {
  panel: DataInputPanel;
  findings: QualifiedFeatureFragment[];
}

interface UseLabPanelsInterface {
  (subGroupId: string, qfs: QualifiedFeatureFragment[]): PanelInterface[];
}

export const useLabPanels: UseLabPanelsInterface = (subGroupId, qfs) => {
  const { panels } = useDataInputPanels();
  const { smartPanels } = useSmartPanels();

  const allPanels = useMemo(() => {
    return new Map([...panels, ...smartPanels]);
  }, [panels, smartPanels]);

  const isLevelLabTest = (finding: QualifiedFeatureFragment) => {
    if (
      finding.qualifiers.filter(
        (q) => q.relation.id === Relations.LEVEL && q.value,
      ).length > 0
    ) {
      return true;
    } else {
      return false;
    }
  };

  const isPositiveNegativeTest = (finding: QualifiedFeatureFragment) => {
    if (finding.negate === true || finding.negate === false) {
      return true;
    } else {
      return false;
    }
  };

  return useMemo(() => {
    const panelsArray = allPanels ? [...allPanels.values()] : [];
    let labsPanelsList = [];

    if (!qfs) {
      return labsPanelsList;
    }

    const otherPanel = {
      title: "Other",
      id: KahunPanelSubGroup.Other,
      findings: [],
      customFindings: [],
    };

    const filteredQfs = qfs.filter(
      (qf) => isLevelLabTest(qf) || isPositiveNegativeTest(qf),
    );

    if (
      (subGroupId === KahunPanelSubGroup.Labs ||
        subGroupId === KahunPanelSubGroup.Procedures) &&
      filteredQfs
    ) {
      filteredQfs.forEach((qf) => {
        const panelIds = panelsArray.filter((panel) =>
          panel.findings.some((f) => qf.feature.id === f.feature.id),
        );

        const panel = panelIds.length > 0 ? panelIds[0] : null;

        const panelExists = labsPanelsList.find(
          (lab) => lab.panel && panel && lab.panel.id === panel.id,
        );

        if (!panel || !panelExists) {
          const panelFindings = panel
            ? filteredQfs.filter((x) =>
                panel.findings.some((y) => y.feature.id === x.feature.id),
              )
            : [qf];

          const labPanelOther = labsPanelsList.find(
            (labPanel) => labPanel.panel.id === KahunPanelSubGroup.Other,
          );

          if (panel === null && labPanelOther) {
            labPanelOther.findings = [
              ...labPanelOther.findings,
              ...panelFindings,
            ];
          } else {
            labsPanelsList.push({
              panel: panel ? panel : otherPanel,
              findings: panelFindings,
            });
          }
        }
      });
    } else {
      labsPanelsList.push({
        panel: null,
        findings: filteredQfs,
      });
    }

    return labsPanelsList;
  }, [allPanels, qfs, subGroupId]);
};
